"use client";

import * as React from "react";
import { Tooltip as TooltipPrimitive } from "radix-ui";

import { cn } from "~/lib/utils";

/**
 * Shadcn-style tooltip wrappers around Radix's primitives. We re-export the
 * raw primitives too so callers that want the full API (anchor, sub-tooltips)
 * don't have to import from `radix-ui` directly.
 *
 * `<TooltipProvider>` is mounted once at the app root, so every `<Tooltip>`
 * shares its hover bookkeeping and the two timings below apply app-wide.
 * Nothing in the app overrides either one.
 */

/**
 * FRONT-2267: how long the pointer must rest on a control before its tooltip
 * opens. 500ms is the long-standing platform convention (Windows' own tooltip
 * delay, and what most design systems settle on) and it is the number that
 * makes a tooltip feel like an answer to a question rather than something
 * chasing the cursor. It was 200ms, which fires while the pointer is still
 * travelling.
 */
const TOOLTIP_OPEN_DELAY_MS = 500;

/**
 * How long after one tooltip closes another may open with NO delay. Radix
 * defaults this to 300ms, and that default is most of what "too fast" feels
 * like: once any tooltip has opened, every control the pointer crosses within
 * the next 300ms pops instantly. On a row of five icon-only buttons — which
 * the Building Session header is — sweeping across it flashes four tooltips
 * nobody asked for.
 *
 * Zero means every control waits its own delay, every time.
 */
const TOOLTIP_SKIP_DELAY_MS = 0;

function TooltipProvider({
  delayDuration = TOOLTIP_OPEN_DELAY_MS,
  skipDelayDuration = TOOLTIP_SKIP_DELAY_MS,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    />
  );
}

/**
 * FRONT-2267: the person's "tooltips on/off" switch.
 *
 * It lives HERE, on the primitive, and not on the `Tip`/`Button` wrappers —
 * that was the first attempt and it was the wrong altitude. Thirteen files
 * compose the raw `Tooltip`/`TooltipTrigger` primitives directly, because a
 * control that is also another `asChild` trigger (a dropdown trigger, a
 * popover trigger) cannot use `Tip` at all (FRONT-1339). Every one of those —
 * the Building Session's "…" menu button and the app sharing control among
 * them — sailed straight past a wrapper-level gate and kept showing hints
 * after the switch said off.
 *
 * `Tooltip` is the one component ALL of them pass through, wrappers included,
 * so gating the root is the only version of this that cannot be bypassed by
 * the next call site someone writes.
 *
 * Forcing `open={false}` rather than hiding the content: a hidden-but-open
 * tooltip would still stamp `aria-describedby` on the trigger and still point
 * it at an element that is not there. Never opening leaves the control
 * exactly as it would be if it had no tooltip at all.
 *
 * Defaults to `true` with no provider mounted, so anything rendered outside
 * the app root (a test, a portal) behaves exactly as it did before this
 * existed.
 */
const TooltipsEnabledContext = React.createContext(true);

function TooltipsEnabledProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <TooltipsEnabledContext.Provider value={enabled}>{children}</TooltipsEnabledContext.Provider>
  );
}

function useTooltipsEnabled(): boolean {
  return React.useContext(TooltipsEnabledContext);
}

function Tooltip({
  open,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const enabled = useTooltipsEnabled();
  // `open` stays undefined (i.e. uncontrolled, Radix's own behaviour) while
  // tooltips are on; switched off it is pinned closed.
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} open={enabled ? open : false} />;
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 4,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "z-50 overflow-hidden rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md",
          "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          "data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1",
          "max-w-xs",
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="fill-popover" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipsEnabledProvider,
  useTooltipsEnabled,
};
